/**
 * AI Chat Types
 *
 * Types for the AI Chat Agent interface and API communication.
 */

export type AIChatMessageRole = 'user' | 'assistant' | 'system' | 'tool';

/**
 * A card that can be embedded in assistant messages for rich UI
 */
export interface AIChatCard {
  type: AIChatCardType;
  data: Record<string, unknown>;
}

export type AIChatCardType =
  | 'account_status'
  | 'campaign_status'
  | 'campaign_summary'
  | 'campaign_statistics'
  | 'statistics'
  | 'confirmation'
  | 'action_buttons'
  | 'wizard_step'
  | 'two_fa_input'
  | 'contract_choice'
  | 'campaign_schedule'
  | 'credentials_request'
  | 'user_ask'
  /** Öffnet einen bestehenden UI-Dialog, statt seine Fragen im Chat nachzubauen. */
  | 'open_dialog'
  | 'import_progress'
  | 'challenge_screenshot'
  | 'contact_list'
  /** Eine Person: Profil, KI-Bewertung mit Begründung, Stand im Ablauf, Nachrichten. */
  | 'contact_detail'
  /** Posteingang live von LinkedIn: Gesprächsliste oder ein Gesprächsverlauf. */
  | 'inbox'
  /** Kamen vom Backend, hatten aber keine Darstellung („Unknown card type“). */
  | 'campaign_cloned'
  /** Kampagnentest — dieselbe Vorschau wie in der App. */
  | 'campaign_test'
  | 'recommendation'
  | 'tutorial'
  | 'error'
  | 'success'
  | 'info';

/**
 * Data for the challenge_screenshot card (2FA/captcha/third-party login
 * challenges). The image is streamed from an authenticated API route.
 */
export interface ChallengeScreenshotCardData {
  accountId: string;
  kind: '2fa' | 'captcha' | 'third_party';
  /** API-v1-relative path of the authed PNG route */
  imageUrl: string;
  capturedAt?: string;
  /** Whether clicking the screenshot should submit coordinates */
  clickable?: boolean;
}

/**
 * Data for the two_fa_input card.
 */
export interface TwoFaInputCardData {
  accountId: string;
  name?: string;
  email?: string;
  status?: string;
  statusLabel?: string;
  /**
   * Which of the three 2FA situations this is. Without them the card claimed "LinkedIn
   * sent a verification code" for app-push challenges too, where no code exists and the
   * only way through is a tap in the LinkedIn app.
   */
  awaitingApproval?: boolean;
  codeAvailable?: boolean;
  canRequestCode?: boolean;
  /** ISO time the current wait runs out. */
  approvalDeadlineAt?: string;
}

/**
 * Data for the contract_choice card: the login waits until one of these is picked.
 */
export interface ContractChoiceCardData {
  accountId: string;
  name?: string;
  email?: string;
  status?: string;
  statusLabel?: string;
  contractNames?: string[];
}

/**
 * Data for the generic statistics card: a titled list of label/value rows.
 */
export interface StatisticsCardData {
  title?: string;
  rows?: Array<{ label: string; value: string | number }>;
  [key: string]: unknown;
}

/**
 * A single chat message
 */
export interface AIChatMessage {
  id: string;
  role: AIChatMessageRole;
  content: string;
  timestamp: Date;
  toolCalls?: Array<{
    id: string;
    function: {
      name: string;
      arguments: string;
    };
  }>;
  toolCallId?: string;
  cards?: AIChatCard[];
  executionMeta?: ChatExecutionMeta;
  isStreaming?: boolean;
}

/**
 * Input for sending a message
 */
export interface SendMessageInput {
  content: string;
  context?: {
    currentPage?: string;
    selectedCampaignId?: string;
    selectedAccountId?: string;
    [key: string]: unknown;
  };
}

/**
 * Response from sending a message
 */
export interface SendMessageResponse {
  message: AIChatMessage;
  suggestions?: string[];
  executionMeta?: ChatExecutionMeta;
}

export type ConversationPhase =
  | 'session_start'
  | 'context_gathering'
  | 'draft_generation'
  | 'proposal'
  | 'accept_save'
  | 'test_execution'
  | 'general';

export interface UserFacingProposal {
  understood: string[];
  missingDetails: string[];
  explanation: string;
  closingQuestion: string;
  actions: Array<'accept' | 'modify' | 'test' | 'continue'>;
}

export interface ExecutionStepTrace {
  plan: string;
  execute: string;
  observe: string;
  decide: string;
  status: 'success' | 'error' | 'info';
}

export interface LogHint {
  label: string;
  tool: string;
  reason: string;
}

export interface ChatExecutionMeta {
  phase: ConversationPhase;
  proposal?: UserFacingProposal;
  steps?: ExecutionStepTrace[];
  logHints?: LogHint[];
}

/**
 * Chat history response
 */
export interface ChatHistoryResponse {
  messages: AIChatMessage[];
  total: number;
  limit: number;
  offset: number;
}

/**
 * Pending action that requires user confirmation
 */
export interface PendingAction {
  actionId: string;
  type: string;
  description: string;
  data?: Record<string, unknown>;
}

/**
 * Action confirmation input
 */
export interface ActionConfirmationInput {
  actionId: string;
  data?: Record<string, unknown>;
}

/**
 * Action confirmation response
 */
export interface ActionConfirmationResponse {
  success: boolean;
  result?: Record<string, unknown>;
  message?: AIChatMessage;
}

/**
 * Chat context for providing relevant information to the AI
 */
export interface ChatContext {
  currentPage?: string;
  selectedCampaignId?: string;
  selectedAccountId?: string;
  recentActions?: string[];
  [key: string]: unknown;
}

/**
 * Suggested actions for quick access
 */
export interface SuggestedAction {
  id: string;
  label: string;
  icon?: string;
  message: string;
  localizedLabels?: Record<string, string>;
  localizedMessages?: Record<string, string>;
}

/**
 * Default suggested actions for new users
 */
export const DEFAULT_SUGGESTED_ACTIONS: SuggestedAction[] = [
  {
    id: 'add-account',
    label: 'Add LinkedIn Account',
    icon: 'person_add',
    message: 'I want to add a new LinkedIn account',
    localizedLabels: { de: 'LinkedIn Account hinzufügen' },
    localizedMessages: { de: 'Ich möchte einen neuen LinkedIn Account hinzufügen' },
  },
  {
    id: 'create-campaign',
    label: 'Create Campaign',
    icon: 'campaign',
    message: 'Help me create a new campaign',
    localizedLabels: { de: 'Kampagne erstellen' },
    localizedMessages: { de: 'Hilf mir eine neue Kampagne zu erstellen' },
  },
  {
    id: 'view-stats',
    label: 'View Statistics',
    icon: 'analytics',
    message: 'Show me my campaign statistics',
    localizedLabels: { de: 'Statistiken anzeigen' },
    localizedMessages: { de: 'Zeig mir meine Kampagnen-Statistiken' },
  },
  {
    id: 'help',
    label: 'Get Help',
    icon: 'help',
    message: 'What can you help me with?',
    localizedLabels: { de: 'Hilfe' },
    localizedMessages: { de: 'Wobei kannst du mir helfen?' },
  },
];

/**
 * Card data types for specific card types
 */
export interface AccountSummary {
  id?: string;
  accountId?: string;
  email?: string;
  name?: string;
  status?: string;
  lastSync?: string;
  lastSynced?: string;
  profileUrl?: string;
}

export interface AccountStatusCardData {
  // Single account fields
  accountId?: string;
  email?: string;
  name?: string;
  status?: string;
  statusLabel?: string;
  lastSynced?: string;
  profileUrl?: string;
  issueDescription?: string;
  suggestedAction?: string;
  proxyLocation?: string;
  // List of accounts (from listLinkedInAccounts)
  accounts?: AccountSummary[];
}

export interface CampaignSummaryCardData {
  campaignId: string;
  name: string;
  type: string;
  status: string;
  contactsCount: number;
  createdAt: string;
}

export interface CampaignStatisticsCardData {
  campaignId: string;
  name: string;
  totalContacts: number;
  connectionsSent: number;
  connectionsAccepted: number;
  messagesSent: number;
  repliesReceived: number;
  replyRate: number;
  connectionRate: number;
}

export interface ConfirmationCardData {
  actionId: string;
  title: string;
  description: string;
  actionType: string;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
}

export interface WizardStepCardData {
  currentStep: number;
  totalSteps: number;
  stepName: string;
  stepDescription: string;
  fields?: Array<{
    name: string;
    label: string;
    type: 'text' | 'select' | 'textarea' | 'number';
    value?: string;
    options?: Array<{ value: string; label: string }>;
    required?: boolean;
  }>;
}

